import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { IMAGES } from "../config/site";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Our Team", to: "/team" },
  { label: "Services", to: "/services" },
  { label: "Client Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

export function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Prevent background scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Close menu on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const transparent = isHome && !scrolled;
  const textColor = transparent ? "text-white" : "text-navy";

  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
        transparent
          ? "bg-transparent"
          : "border-b border-navy/10 bg-warmwhite/90 backdrop-blur-xl shadow-[0_1px_20px_rgba(10,25,47,0.06)]"
      }`}
      data-testid="site-navbar"
    >
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" aria-label="Nolen Law home">
          <span className={`flex items-center ${transparent ? "rounded-sm bg-white/95 px-2 py-1 shadow-sm" : ""}`}>
            <img src={IMAGES.logo} alt="The Law Office of Ronald K. Nolen, LLC logo" className="h-11 w-auto" />
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group relative text-sm font-500 tracking-wide transition-colors duration-300 ${textColor} ${
                  isActive ? "opacity-100" : "opacity-80 hover:opacity-100"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-consultation"
            className="group inline-flex items-center gap-2 rounded-sm bg-gold px-6 py-3 text-sm font-600 text-navy transition-colors duration-300 hover:bg-gold-soft"
          >
            Request Consultation
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className={`lg:hidden ${textColor}`}
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <Menu className="h-7 w-7" strokeWidth={1.6} />
        </button>
      </nav>
    </header>

    {/*
      Mobile drawer rendered as a SIBLING of the header (not a descendant).
      The header uses backdrop-blur when scrolled, which creates a containing
      block that would trap a fixed-position child and prevent it from covering
      the viewport. Rendering the drawer outside the header keeps it a true
      viewport-fixed overlay with an OPAQUE background in every state.
    */}
    <div
      className={`fixed inset-0 z-[70] lg:hidden ${mobileOpen ? "pointer-events-auto" : "pointer-events-none"}`}
      aria-hidden={!mobileOpen}
    >
      <div
        className={`absolute inset-0 bg-navy/50 transition-opacity duration-300 ${mobileOpen ? "opacity-100" : "opacity-0"}`}
        onClick={() => setMobileOpen(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        style={{ backgroundColor: "#FAFAF8" }}
        className={`absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col shadow-2xl transition-transform duration-400 ease-out ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu-panel"
      >
        <div className="flex h-20 items-center justify-between border-b border-navy/10 px-5">
          <img src={IMAGES.logo} alt="Nolen Law" className="h-10 w-auto" />
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            data-testid="mobile-menu-close"
            className="text-navy"
          >
            <X className="h-7 w-7" strokeWidth={1.6} />
          </button>
        </div>
        <div className="flex flex-1 flex-col gap-1 overflow-y-auto px-5 py-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `border-b border-navy/10 py-4 font-serif text-3xl font-500 transition-colors ${
                  isActive ? "text-gold" : "text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>
        <div className="px-5 pb-10">
          <Link
            to="/contact"
            data-testid="mobile-cta-consultation"
            className="flex w-full items-center justify-center gap-2 rounded-sm bg-gold px-6 py-4 text-base font-600 text-navy"
          >
            Request Consultation
            <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}
